import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@shared/constants';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { getSessionIdV2 } from '../PendingDashboard/services';
import { UsersTableRow } from './types';

const useVideoChatV2 = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const launchInProgressRef = useRef(false);

  const startVideoChat = useCallback(async (user: UsersTableRow) => {
    if (launchInProgressRef.current) return;

    launchInProgressRef.current = true;
    setPendingUserId(user.id);
    const meetingTab = window.open('', '_blank');
    try {
      const sessionId = await getSessionIdV2([user.id.toString()]);
      if (!sessionId) throw new Error('Video chat session creation failed');

      const meetingUrl = `${ROUTES.VIDEO_CHAT}/${sessionId}`;
      if (meetingTab) {
        meetingTab.location.href = meetingUrl;
        meetingTab.focus();
      } else {
        window.open(meetingUrl, '_blank')?.focus();
      }
    } catch {
      meetingTab?.close();
      dispatch(openPopUp(POPUP_TYPES.ALERT, {
        titleText: t('UsersTable.quickActions.videoUnavailableTitle'),
        bodyText: t('UsersTable.quickActions.videoUnavailableBody'),
        buttonText: t('UsersTable.quickActions.close'),
      }));
    } finally {
      launchInProgressRef.current = false;
      setPendingUserId(null);
    }
  }, [dispatch, t]);

  const confirmVideoChat = useCallback((user: UsersTableRow) => {
    const fullName = `${user.firstName} ${user.lastName}`.trim();
    dispatch(openPopUp(POPUP_TYPES.CONFIRM, {
      titleText: t('UsersTable.quickActions.confirmTitle'),
      bodyText: t('UsersTable.quickActions.confirmBody', { name: fullName }),
      confirmText: t('UsersTable.quickActions.approve'),
      cancelText: t('UsersTable.quickActions.cancelMeeting'),
      onConfirm: (confirmed: boolean) => {
        if (confirmed) startVideoChat(user);
      },
    }));
  }, [dispatch, startVideoChat, t]);

  return {
    confirmVideoChat,
    pendingUserId,
    isStarting: pendingUserId !== null,
  };
};

export default useVideoChatV2;